/**
 * The Heuristic Systems crow.
 *
 * Uses `crow-mask.png` through a CSS mask rather than the pre-rendered
 * `crow-dark` / `crow-light` files, per `brand/README.md`. The mask takes the
 * colour of whatever it is drawn on, so **one file covers light mode, dark mode
 * and every accent** — no theme variants to ship and none to forget to update.
 *
 * Both the `-webkit-` and unprefixed mask properties are set; Safari still
 * wants the prefix.
 *
 * The brand guidance is to pair the mark with the wordmark except where space
 * genuinely forbids it. `LogoLockup` is the default; the bare `LogoMark` is for
 * the rail, where 56px leaves no room for words.
 */

const MASK = "url('/brand/crow-mask.png')";

const maskStyle: React.CSSProperties = {
  WebkitMaskImage: MASK,
  maskImage: MASK,
  WebkitMaskSize: 'contain',
  maskSize: 'contain',
  WebkitMaskPosition: 'center',
  maskPosition: 'center',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
};

/**
 * The mark on its own. Decorative by default — when it sits beside the
 * wordmark the text carries the accessible name.
 *
 * Colour comes from `currentColor`, so set `text-*` on this element or any
 * ancestor.
 */
export function LogoMark({ className = 'h-8 w-8' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block bg-current shrink-0 ${className}`}
      style={maskStyle}
    />
  );
}

/**
 * Mark plus wordmark. The brand guidance is that the mark is not yet
 * recognisable enough to stand alone, so this is what should normally be used.
 */
export default function LogoLockup({
  markClassName = 'h-8 w-8',
  textClassName = 'text-base font-semibold tracking-tight',
  subtitle,
  className = '',
}: {
  markClassName?: string;
  textClassName?: string;
  subtitle?: string;
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <LogoMark className={markClassName} />
      <span className="leading-tight">
        <span className={textClassName}>Heuristic Systems</span>
        {subtitle && (
          <span className="block text-xs font-normal opacity-70">{subtitle}</span>
        )}
      </span>
    </span>
  );
}
